import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CreditAuditEntry {
  id: string;
  device_id: string;
  device_name?: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  assignment_type: string;
  action: string;
  created_at: string;
  metadata?: {
    allocated_by?: string;
    credits_allocated?: number;
    package_name?: string;
    allocation_timestamp?: string;
    new_device_credits?: number;
    previous_device_credits?: number;
    package_id?: string;
    order_id?: string;
    organization_id?: string;
  };
}

export const useCreditAudit = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['credit-audit', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get user's organization ID first
      const { data: userOrganization } = await supabase
        .from('user_roles')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userOrganization?.organization_id) return [];

      // Fetch audit entries first
      const { data: auditEntries, error } = await supabase
        .from('device_assignment_audit')
        .select('*')
        .eq('action', 'manual_credit_allocation')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching credit audit:', error);
        throw error;
      }

      if (!auditEntries || auditEntries.length === 0) {
        return [];
      }

      // Get unique user IDs and device IDs for separate queries
      const userIds = [...new Set(auditEntries.map(entry => entry.user_id).filter(Boolean))];
      const deviceIds = [...new Set(auditEntries.map(entry => entry.device_id).filter(id => id !== 'USER_OWNED'))];

      // Fetch user details
      const { data: userProfiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      // Fetch device details
      const { data: deviceDetails } = await supabase
        .from('device_details')
        .select('device_id, device_name')
        .in('device_id', deviceIds);

      // Create lookup maps
      const userMap = new Map(userProfiles?.map(user => [user.id, user]) || []);
      const deviceMap = new Map(deviceDetails?.map(device => [device.device_id, device]) || []);

      // Transform and filter by organization
      const transformedEntries: CreditAuditEntry[] = auditEntries
        .map(entry => {
          const userProfile = entry.user_id ? userMap.get(entry.user_id) : null;
          const deviceDetail = deviceMap.get(entry.device_id);
          
          return {
            id: entry.id,
            device_id: entry.device_id,
            device_name: deviceDetail?.device_name,
            user_id: entry.user_id,
            user_name: userProfile ? 
              `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Unknown User' :
              'System',
            user_email: userProfile?.email,
            assignment_type: entry.assignment_type,
            action: entry.action,
            created_at: entry.created_at,
            metadata: entry.metadata as CreditAuditEntry['metadata']
          };
        })
        .filter(entry => {
          // Filter by organization if metadata contains organization_id
          if (entry.metadata?.organization_id) {
            return entry.metadata.organization_id === userOrganization.organization_id;
          }
          return false; // Only show entries with matching organization_id
        });

      return transformedEntries;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
};